import { FC, useState } from 'react';
import { DiagnosticsCompletedStudentsQuery } from '../../graphQLOperations/__generated__/DiagnosticsCompletedStudentsQuery.graphql';
import { PreloadedQuery, usePreloadedQuery } from 'react-relay';
import {
  getDiagnosticStatusByClassReport,
  getStudentsCompletedDiagnosticInfo,
  getStudentsNotCompletedDiagnosticInfo,
} from '../../graphQLOperations/Diagnostics';
import StudentDiagnosticModal from './StudentDiagnosticModal';
import { DiagnosticStatus, excludeInProgressStudents, filterStudentsByStatus } from './reportHelper';
import { splitId } from '../../businessLogicService/splitIdsFromBackEnd';
import { DiagnosticsStatusByClassReportQuery } from '../../graphQLOperations/__generated__/DiagnosticsStatusByClassReportQuery.graphql';

interface Props {
  completedQueryRef: PreloadedQuery<DiagnosticsCompletedStudentsQuery, Record<string, unknown>>;
  notCompletedQueryRef: PreloadedQuery<DiagnosticsCompletedStudentsQuery, Record<string, unknown>>;
  inProgressQueryRef?: PreloadedQuery<DiagnosticsStatusByClassReportQuery, Record<string, unknown>>;
}

enum TabOption {
  COMPLETED,
  IN_PROGRESS,
  NOT_STARTED,
}

export const DiagnosticReportCards: FC<Props> = ({ completedQueryRef, notCompletedQueryRef, inProgressQueryRef }) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [initialTab, setInitialTab] = useState<TabOption>(TabOption.COMPLETED);

  const completeDiagnosticStudents = usePreloadedQuery(getStudentsCompletedDiagnosticInfo, completedQueryRef);
  const notStartedDiagnosticStudents = usePreloadedQuery(getStudentsNotCompletedDiagnosticInfo, notCompletedQueryRef);
  const inProgressDiagnosticStudents = inProgressQueryRef
    ? usePreloadedQuery(getDiagnosticStatusByClassReport, inProgressQueryRef)
    : null;

  const inProgressStudents = inProgressDiagnosticStudents
    ? filterStudentsByStatus(inProgressDiagnosticStudents.classDiagnosticStatusReport, DiagnosticStatus.IN_PROGRESS)
    : [];
  const inProgressIds = new Set(inProgressStudents.map(report => splitId(report.student.id)));
  const notStartedStudents = excludeInProgressStudents(notStartedDiagnosticStudents, inProgressIds);

  const openModal = (tab: TabOption) => {
    setInitialTab(tab);
    setModalVisible(true);
  };

  return (
    <>
      <div className="tw-flex tw-flex-col tw-justify-center lg:tw-flex-row tw-w-full lg:tw-gap-x-[20px] tw-gap-y-4 ">
        <button
          className="tw-bg-white lg:tw-w-1/3 tw-flex tw-flex-row tw-items-center tw-gap-x-4 tw-p-4 tw-rounded-xl tw-border-solid tw-border-neutral-200 tw-border-b-[8px] hover:tw-border-indigo-200 tw-cursor-pointer"
          onClick={() => openModal(TabOption.COMPLETED)}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <circle cx="10" cy="10" r="10" fill="#42C5A6" />
          </svg>
          <span className="tw-text-4xl tw-font-bold tw-font-atkinson tw-text-neutral-950">
            {completeDiagnosticStudents.allStudents?.nodes?.length}
          </span>
          <span className="tw-text-xl tw-font-atkinson tw-text-neutral-950 tw-font-bold">Completed</span>
        </button>
        <button
          className="tw-bg-white lg:tw-w-1/3 tw-flex tw-flex-row tw-items-center tw-gap-x-4 tw-p-4 tw-rounded-xl tw-border-solid tw-border-neutral-200 tw-border-b-[8px] hover:tw-border-indigo-200 tw-cursor-pointer"
          onClick={() => openModal(TabOption.IN_PROGRESS)}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <circle cx="10" cy="10" r="10" fill="#5776FC" />
          </svg>
          <span className="tw-text-4xl tw-font-bold tw-font-atkinson tw-text-neutral-950">
            {inProgressStudents.length}
          </span>
          <span className="tw-text-xl tw-font-atkinson tw-text-neutral-950 tw-font-bold">In progress</span>
        </button>
        <button
          className="tw-bg-white lg:tw-w-1/3 tw-flex tw-flex-row tw-items-center tw-gap-x-4 tw-p-4 tw-rounded-xl tw-border-solid tw-border-neutral-200 tw-border-b-[8px] hover:tw-border-indigo-200 tw-cursor-pointer"
          onClick={() => openModal(TabOption.NOT_STARTED)}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <circle cx="10" cy="10" r="10" fill="#D3D3D3" />
          </svg>
          <span className="tw-text-4xl tw-font-bold tw-font-atkinson tw-text-neutral-950">
            {notStartedStudents.length}
          </span>
          <span className="tw-text-xl tw-font-atkinson tw-text-neutral-950 tw-font-bold">Not started</span>
        </button>
      </div>
      <StudentDiagnosticModal
        visible={modalVisible}
        setVisible={setModalVisible}
        initialTab={initialTab}
        completeDiagnosticStudents={completeDiagnosticStudents}
        notStartedDiagnosticStudents={notStartedStudents.map(student => ({
          id: student?.id ?? '',
          firstName: student?.firstName ?? '',
          lastName: student?.lastName ?? '',
          publicUserId: student?.publicUserId ?? '',
        }))}
        inProgressDiagnosticStudents={inProgressStudents.map(student => ({
          id: student.student.id,
          firstName: student.student.firstName ?? '',
          lastName: student.student.lastName ?? '',
          publicUserId: student.student.publicUserId ?? '',
          questionsAnswered: student.questionsAnswered ?? 0,
        }))}
        inProgressReports={inProgressStudents}
      />
    </>
  );
};
